import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { setParamsToStorage } from "@/shared/lib/storage-params"

import { useHandLandmarker } from "../hooks/use-hand-landmarker"
import { generatePalmReading, persistPalmReading } from "../lib/palm-reading"
import { CameraFlash } from "@/packages/camera"
import { CreateReportModal } from "./create-report-modal"
import { PalmCamera } from "./palm-camera"
import { PalmInstruction } from "./palm-instruction"
import { PalmPhotoResult } from "./palm-photo-result"
import { ReportBeingPrepared } from "./report-being-prepared"

type Step = "intro" | "instruction" | "camera" | "flash" | "result" | "prepared"

/** Palm scan flow: intro → instruction → camera → flash → result → prepared. Left palm only. */
export function ReportQuiz() {
  const navigate = useNavigate()
  const { setIsPalmReadingLocked } = useChat()
  const { landmarker, ready } = useHandLandmarker()

  const [step, setStep] = useState<Step>("intro")
  const [photo, setPhoto] = useState<string | null>(null)

  const backToMain = () => navigate({ to: WebappRoutes.ADVISORS })

  const showFlash = (dataUrl: string) => {
    setPhoto(dataUrl)
    setStep("flash")
  }

  // Gallery upload has no shutter, so skip the flash and go straight to result.
  const showResult = (dataUrl: string) => {
    setPhoto(dataUrl)
    setStep("result")
  }

  const handleScanComplete = () => {
    persistPalmReading(generatePalmReading())
    setParamsToStorage({ hand_photo_palmistry: new Date().toUTCString() })
    setIsPalmReadingLocked(true)
    setStep("prepared")
  }

  switch (step) {
    case "instruction":
      return (
        <PalmInstruction
          onBack={() => setStep("intro")}
          onTakePhoto={() => setStep("camera")}
          onPhotoSelected={showResult}
        />
      )
    case "camera":
      return (
        <PalmCamera
          onBack={() => setStep("instruction")}
          onCaptured={showFlash}
        />
      )
    case "flash":
      return <CameraFlash onComplete={() => setStep("result")} />
    case "result":
      return photo ? (
        <PalmPhotoResult
          imageSrc={photo}
          landmarker={landmarker}
          ready={ready}
          onContinue={handleScanComplete}
          onRetake={() => setStep("camera")}
          onBack={() => setStep("camera")}
        />
      ) : null
    case "prepared":
      return <ReportBeingPrepared onDone={backToMain} />
    default:
      return (
        <CreateReportModal onNotNow={backToMain} onStart={() => setStep("instruction")} />
      )
  }
}
